import { ReactElement } from "react"
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import { IGroupForm, IQuestionForm } from "interfaces/xlsForm"
import { FormField } from "interfaces/mappingInterfaces"
import useCommon from "hooks/useCommon"
import {
  useGetXlsFormFieldsWithGroupsQuery,
  useUpdatePersonalInfoMutation,
  useSaveGroupMutation,
  useSaveQuestionMutation,
} from "services/household/xlsFormSchemas"
import FormList from "components/Household/Form/FormList"

interface Props {
  xlsFormId: number
  permissions: boolean
}

const BaseXlsFormList = ({ xlsFormId, permissions }: Props): ReactElement => {
  const { showSnackBar } = useCommon()

  const { data: baseList } = useGetXlsFormFieldsWithGroupsQuery({ xlsFormId }, { skip: !xlsFormId })
  const [updatePersonalInfo] = useUpdatePersonalInfoMutation()
  const [saveQuestion, { isLoading: loadingSaveQuestion }] = useSaveQuestionMutation()
  const [saveGroup, { isLoading: loadingSaveGroup }] = useSaveGroupMutation()

  const handleUpdatePersonalInfo = async (field: FormField): Promise<void> => {
    if (!permissions) return

    try {
      await updatePersonalInfo({
        xlsFormFieldId: field.fieldId,
        isPersonalInfo: !field.isPersonalInfo,
      }).unwrap()
      showSnackBar("Personal info updated successfully")
    } catch (error) {
      showSnackBar("Personal info update failed", "error")
    }
  }

  const onQuestionSave = async (payload: IQuestionForm, onClose: () => void): Promise<void> => {
    try {
      await saveQuestion({ xlsFormId, ...payload }).unwrap()
      showSnackBar("Question saved successfully")
      onClose()
    } catch (err) {
      const error = err as FetchBaseQueryError
      showSnackBar((error?.data as { message: string })?.message ?? "Question save failed", "error")
    }
  }

  const onGroupSave = async (payload: IGroupForm, onClose: () => void): Promise<void> => {
    try {
      await saveGroup({ xlsFormId, ...payload }).unwrap()
      showSnackBar("Group saved successfully")
      onClose()
    } catch (err) {
      const error = err as FetchBaseQueryError
      showSnackBar((error?.data as { message: string })?.message ?? "Group save failed", "error")
    }
  }

  return (
    <FormList
      xlsFormId={xlsFormId}
      permissions={permissions}
      saveQuestion={onQuestionSave}
      saveGroup={onGroupSave}
      onUpdatePersonalInfo={handleUpdatePersonalInfo}
      loadingSaveQuestion={loadingSaveQuestion}
      loadingSaveGroup={loadingSaveGroup}
      list={baseList ?? []}
    />
  )
}

export default BaseXlsFormList
